import React, { useState } from 'react';
import { handleRowPaste, PASTE_ERROR_STYLE } from '../utils/pasteRow';   // 2026-10-26: paste a row copied from Excel
import PasteHint from '../utils/PasteHint';   // the tip + its "i" (one owner)
import { groupByYear, getYear, formatMonthLabel } from '../engines/dateUtils';
import { r2 } from '../engines/drRecognition';

// RenewalControls -- the ONE renewal control for AE Quota (Deferred Revenue page) and Company
// Quota (Revenue Build page). W-005, 2026-09-23. It replaces two hand-rolled copies that drifted
// in their wording and hardcoded a 24-month grid.
//
// ONE MODULE, THREE MOUNT POINTS. The pages place the pieces differently: on the AE panel the
// mode select and churn input sit in a row with Start Mode, and the Existing Revenue Inputs block
// renders BETWEEN that row and the manual grid. A single element would move the grid; so the
// module exports the select, the churn input and the grid, and each page mounts them where the
// old copies were. Labels, style, parsing and the grid are owned HERE, once.
//
// THE GRID SPAN IS THE CALLER'S `months` -- the page's existing forecastMonths
// (filterForecast(allMonths, cutoff)), which is exactly the set of months the DR engines read
// manual_renewals for (drRecognition.computeRenewalLookback, called only for m > closeMonth).
// It used to be 24 months from the first forecast month: the pre-8f0b67e ENGINE span, left behind
// when the engines moved to the caller's window. That drew 15 columns no engine reads on every
// 9-month tenant, and a window longer than 24 months would have left read months with no column.
//
// EACH PAGE KEEPS ITS OWN STATE, LOAD AND SAVE. These components only read the value they are
// given and call the setter they are given, so the save payload for the same input cannot change.
//
// THE BLANK/ZERO CONTRACT, preserved exactly from both copies: a 0 renders as "", a cleared cell
// stores 0, onBlur merges {...prev, [month]: value}, parse parseFloat(value.replace(/,/g, "")) || 0.
// In manual mode a missing key and a 0 both mean "no renewal this month" -- there is no third state.
//
// THE WORDING (Brian, 2026-09-23) -- one label set for both modules, because both engines renew the
// SAME quantity through the one drRecognition.computeRenewalLookback: in auto mode the renewal in
// month m is the contract value booked in month m-12 (totalACV[m-12], or existing_total_acv[m-12]
// for a closed month) -- measured, never ARR. So:
//   * "Auto (contract value booked 12 months earlier)" -- replaces "Auto (prior 12-month ACV)" and
//     "Auto (prior 12-month ARR)". Both old labels also read as a twelve-month TOTAL; the engine takes
//     ONE month. The CQ "ARR" wording had been wrong since 7fab7ec (2026-04-06): the CQ engine never
//     read ARR (the run-rate level) for renewals.
//   * "% of renewing contract value that cancels" -- replaces "...ACV that does not renew" and
//     "...ARR that does not renew". "CANCELS" IS ACCURATE, and must not be "corrected" to "does not
//     renew": a client may give cancellation notice at any time, but contracts are paid up front and
//     run to the end of their 12-month term, so a cancellation takes effect AT RENEWAL -- which is
//     exactly when the engine applies churn (churn = grossRenewals x churnRate, in the renewal month).
//   * The grid keeps "Monthly Renewal ACV" and its help text: ACV is the house term on both pages'
//     input labels ("Total ACV (actuals)").
//
// STYLE: one set, taken from the Deferred Revenue page's copy (its inputs sit beside that page's
// Start Mode select). The Revenue Build copy used that page's own input style; it now matches.

const iS = { width: "100%", fontSize: 11, padding: "5px 8px", borderRadius: 4, border: "1px solid #D1D5DB", fontFamily: "'Space Mono',monospace", textAlign: "right" };
const lblS = { fontSize: 10, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid #E2E5F0" };
const helpS = { fontSize: 9, color: "#8B8FA3", marginTop: 4 };

export function RenewalModeSelect({ mode, onModeChange }) {
  return (
    <div style={{ minWidth: 200 }}>
      <div style={lblS}>Renewal Mode</div>
      <select value={mode} onChange={e => onModeChange(e.target.value)} style={{ ...iS, textAlign: "left", cursor: "pointer" }}>
        <option value="auto">Auto (contract value booked 12 months earlier)</option>
        <option value="manual">Manual Schedule</option>
      </select>
    </div>
  );
}

// churn_rate is a FRACTION on both settings rows (0.15 = 15%), loaded with parseFloat(...) || 0
// and saved as-is by both pages; the input shows and accepts a percentage.
export function ChurnRateInput({ churnRate, onChurnRateChange }) {
  return (
    <div style={{ minWidth: 140 }}>
      <div style={lblS}>Annual Churn Rate</div>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <input type="text" value={churnRate > 0 ? String(r2(churnRate * 100)) : ""}
          onChange={e => { const raw = e.target.value.replace(/[^0-9.]/g, ""); if (raw === "") { onChurnRateChange(0); return; } const v = parseFloat(raw); if (!isNaN(v)) onChurnRateChange(r2(v / 100)); }}
          onFocus={e => { if (churnRate === 0) e.target.value = ""; e.target.select(); }}
          onBlur={e => { if (e.target.value === "") onChurnRateChange(0); }}
          placeholder="0" style={{ ...iS, width: 80 }} />
        <span style={{ fontSize: 11, color: "#6B7280" }}>%</span>
      </div>
      <div style={helpS}>% of renewing contract value that cancels</div>
    </div>
  );
}

export function ManualRenewalsGrid({ mode, renewals, onRenewalsChange, months }) {
  const [pasteError, setPasteError] = useState(null);   // before the early return: the hook order stays fixed when the mode changes
  if (mode !== 'manual') return null;
  const byYear = groupByYear(months || []);
  return (
    <div style={{ marginBottom: 16, padding: 12, background: "#FAFBFE", borderRadius: 6, border: "1px solid #E2E5F0" }}>
      <div style={lblS}>Monthly Renewal ACV</div>
      <div style={{ ...helpS, marginTop: 0, marginBottom: 8 }}>Enter the renewal ACV amount for each month. Leave blank for months with no renewals.</div>
      <PasteHint />
      {Object.keys(byYear).map(Number).sort((a, b) => a - b).map(yr => (
        <div key={yr} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: "#4B5563", marginBottom: 4 }}>{yr}</div>
          <div style={{ display: "flex", gap: 4 }}>
            {months.filter(m => getYear(m) === yr).map(m => (
              <div key={m} style={{ flex: 1, minWidth: 0, maxWidth: 80 }}>
                <div style={{ fontSize: 8, color: "#8B8FA3", textAlign: "center", marginBottom: 2 }}>{formatMonthLabel(m, "short")}</div>
                <input type="text" defaultValue={(renewals[m] || 0) ? (renewals[m] || 0).toLocaleString("en-US") : ""} key={"mr-" + m + "-" + (renewals[m] || 0)}
                  onFocus={e => { const v = renewals[m] || 0; e.target.value = v ? String(v) : ""; e.target.select(); }}
                  onBlur={e => { const v = parseFloat(e.target.value.replace(/,/g, "")) || 0; onRenewalsChange(p => ({ ...p, [m]: v })); e.target.value = v ? v.toLocaleString("en-US") : ""; }}
                  onPaste={e => handleRowPaste(e, { months, month: m, onError: setPasteError,   // a pasted row: each value stored as typed (a blank cell = 0, as typing blank)
                    apply: cells => onRenewalsChange(p => { const n = { ...p }; for (const [mo, v] of cells) n[mo] = v === null ? 0 : v; return n; }) })}
                  placeholder="0" style={{ ...iS, width: "100%", padding: "3px 4px", fontSize: 9 }} />
              </div>
            ))}
          </div>
        </div>
      ))}
      {pasteError && <div data-paste-error="" style={PASTE_ERROR_STYLE}>{pasteError}</div>}
    </div>
  );
}
